/**
 * localStorage 配额自愈
 *
 * 背景（真实事故）：画布页把整份节点快照写 localStorage（每次自动保存都写），
 * 加上 redux-persist 的生成任务历史常驻，单源 5MB 很容易吃满。写满之后
 * setItem 抛 QuotaExceededError，凡是 try { setItem } catch {} 的地方全部静默失效：
 * 画布本地备份没了、跨标签写锁写不进去（多标签防覆盖整体失灵）、persist 每次 dispatch 抛错刷屏。
 *
 * 原则：
 * 1. 只清「可再生」的数据：画布旧快照（服务端才是权威）、详情 payload 缓存；
 *    登录态 / 用户信息 / 用户上传草稿一律不碰。
 * 2. 写入失败先腾地方再重试一次，仍失败就放弃并告警，绝不冒泡成崩溃。
 * 3. 画布快照设体积上限 + 份数上限——它只是 API 失败时的兜底，不值得撑爆整个源。
 */

/** 画布快照 key 前缀（见 useCanvasPersistence 的 LOCAL_KEY） */
const CANVAS_SNAPSHOT_PREFIX = 'amai_canvas_';
/** 画布协调用的运行时 key（写锁 / 广播消息），不属于快照，清理必须跳过 */
const CANVAS_RUNTIME_PREFIXES = ['amai_canvas_lock_', 'amai_canvas_msg_', 'amai_canvas_ch_'];
/** 可再生缓存：配额告急时可丢，下次进页面重新拉 */
const DISPOSABLE_PREFIXES = ['last_video_detail_payload_', 'mask_drawing_cache'];
/** 单份画布快照上限（字符数 ≈ 字节数）：超了就不写，避免一份快照顶满配额 */
const SNAPSHOT_MAX_CHARS = 1_500_000;
/** 最多保留几份画布快照（按 updatedAt 从新到旧） */
const SNAPSHOT_KEEP = 3;
/** 软上限：常见浏览器单源 5MB，超过 80% 就主动减负 */
const SOFT_LIMIT_BYTES = 4 * 1024 * 1024;

function isQuotaError(e: unknown): boolean {
  if (!e) return false;
  const err = e as { name?: string; code?: number };
  return (
    err.name === 'QuotaExceededError' ||
    err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    err.code === 22 ||
    err.code === 1014
  );
}

/** 当前 localStorage 已用字符数（≈字节数，UTF-16 下会略偏小，够用） */
function localStorageBytes(): number {
  try {
    let total = 0;
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key) continue;
      total += key.length + (localStorage.getItem(key)?.length ?? 0);
    }
    return total;
  } catch {
    return 0;
  }
}

interface SnapshotEntry { key: string; updatedAt: number; bytes: number }

/**
 * 从快照 JSON 里取 updatedAt，**不整体 JSON.parse**。
 * 快照动辄上 MB，而这段代码在每次自动保存（500ms debounce）后都要跑，
 * JSON.parse 会构建整个对象树，大画布上会直接拖慢保存。
 * 快照字段顺序为 canvasId → nodes → edges → viewport → updatedAt，
 * 顶层 updatedAt 一定在最后，故用 lastIndexOf，不会误中节点 data 里的同名字段。
 */
function readUpdatedAt(raw: string): number {
  const idx = raw.lastIndexOf('"updatedAt":');
  if (idx < 0) return 0;
  const m = /^"updatedAt":(\d+)/.exec(raw.slice(idx, idx + 32));
  return m ? Number(m[1]) : 0;
}

/** 列出所有画布快照（不含写锁 / 广播消息） */
function listCanvasSnapshots(): SnapshotEntry[] {
  const list: SnapshotEntry[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(CANVAS_SNAPSHOT_PREFIX)) continue;
      if (CANVAS_RUNTIME_PREFIXES.some(p => key.startsWith(p))) continue;
      const raw = localStorage.getItem(key) ?? '';
      list.push({ key, updatedAt: readUpdatedAt(raw), bytes: key.length + raw.length });
    }
  } catch {
    return [];
  }
  return list;
}

/**
 * 按「新 → 旧」保留 keep 份画布快照，其余删除。
 * @param keepKey 本次正在写的 key，永远保留且不占 keep 名额
 * @returns 释放的字符数
 */
export function pruneCanvasSnapshots(keep: number = SNAPSHOT_KEEP, keepKey?: string): number {
  const list = listCanvasSnapshots().sort((a, b) => b.updatedAt - a.updatedAt);
  let kept = keepKey ? 1 : 0;
  let freed = 0;
  for (const item of list) {
    if (item.key === keepKey) continue;
    if (kept < keep) { kept += 1; continue; }
    try {
      localStorage.removeItem(item.key);
      freed += item.bytes;
    } catch { /* 忽略单项失败 */ }
  }
  return freed;
}

/** 腾地方：先砍旧画布快照（只留最新一份），不够再丢可再生缓存 */
export function freeSpaceFor(needBytes: number = 0): number {
  let freed = pruneCanvasSnapshots(1);
  if (freed >= needBytes) return freed;
  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (!key || !DISPOSABLE_PREFIXES.some(p => key.startsWith(p))) continue;
      const bytes = key.length + (localStorage.getItem(key)?.length ?? 0);
      localStorage.removeItem(key);
      freed += bytes;
    }
  } catch { /* 忽略 */ }
  return freed;
}

/** 淘汰节流：自动保存很频繁，没必要每笔都全量遍历一遍 localStorage */
let lastPruneAt = 0;
const PRUNE_MIN_INTERVAL_MS = 30_000;

function pruneThrottled(keepKey: string): void {
  const now = Date.now();
  if (now - lastPruneAt < PRUNE_MIN_INTERVAL_MS) return;
  lastPruneAt = now;
  pruneCanvasSnapshots(SNAPSHOT_KEEP, keepKey);
}

/** 写入画布快照：限体积、失败腾地方重试、成功后按份数淘汰旧的 */
export function writeCanvasSnapshot(
  key: string,
  value: string,
): { ok: boolean; reason?: 'too_large' | 'quota' } {
  if (value.length > SNAPSHOT_MAX_CHARS) return { ok: false, reason: 'too_large' };
  try {
    localStorage.setItem(key, value);
    pruneThrottled(key);
    return { ok: true };
  } catch (e) {
    if (!isQuotaError(e)) return { ok: false };
  }
  freeSpaceFor(value.length * 2);
  try {
    localStorage.setItem(key, value);
    pruneThrottled(key);
    return { ok: true };
  } catch {
    return { ok: false, reason: 'quota' };
  }
}

/**
 * redux-persist 容错 storage：配额满时先腾地方 → 重试 → 仍失败则放弃本轮并告警，
 * 避免每次 dispatch 抛 QuotaExceededError 刷屏、以及 persist 永久写不进去。
 */
export const resilientPersistStorage = {
  getItem: (key: string): Promise<string | null> => {
    try {
      return Promise.resolve(localStorage.getItem(key));
    } catch {
      return Promise.resolve(null);
    }
  },
  setItem: (key: string, value: string): Promise<void> => {
    try {
      localStorage.setItem(key, value);
      return Promise.resolve();
    } catch (e) {
      if (!isQuotaError(e)) {
        console.warn('[storage] persist 写入失败（非配额原因）:', e);
        return Promise.resolve();
      }
    }
    const freed = freeSpaceFor(value.length * 2);
    try {
      localStorage.setItem(key, value);
    } catch {
      console.warn(`[storage] 配额不足，persist 本轮写入已跳过（已腾出 ${(freed / 1024).toFixed(0)}KB）`);
    }
    return Promise.resolve();
  },
  removeItem: (key: string): Promise<void> => {
    try {
      localStorage.removeItem(key);
    } catch { /* 忽略 */ }
    return Promise.resolve();
  },
};

/** 启动自愈：老用户机器配额可能早已写满，启动时收敛快照、腾出空间 */
export function bootstrapLocalStorage(): void {
  try {
    const used = localStorageBytes();
    const freed = used > SOFT_LIMIT_BYTES ? pruneCanvasSnapshots(2) : pruneCanvasSnapshots(SNAPSHOT_KEEP);
    if (freed > 0 || used > SOFT_LIMIT_BYTES) {
      console.warn(
        `[storage] localStorage 已用 ${(used / 1024 / 1024).toFixed(2)}MB，清理旧画布快照 ${(freed / 1024).toFixed(0)}KB`,
      );
    }
  } catch { /* 静默 */ }
}
